<!-- eslint-disable vue/no-v-html -->
<template>
  <div>
    <!-- Secondary Owner details -->
    <div class="pvd-container container-divider">
      <h2 class="pvd-heading-L">
        {{ pd_content.rc_secondary_user_details.basic_info_heading }}
        <EditLink
          page-name="joint-personal-information"
          is-not-istock="true"
        />
      </h2>
      <apx-full-name
        :group-label="pd_content.rc_secondary_user_details.basic_info_name_label"
        :first-name="su_firstName"
        :middle-name="su_middleName"
        :last-name="su_lastName"
        readonly
      />
      <apx-ssn
        :label="pd_content.rc_secondary_user_details.basic_info_ssn_label"
        :value="su_ssn"
        readonly
      />
      <apx-date-select
        :group-label="pd_content.rc_secondary_user_details.basic_info_dob_label"
        :date-input-value="su_dob"
        readonly
      />
    </div>
    <!-- Personal information details -->
    <div class="pvd-container container-divider">
      <h2 class="pvd-heading-L">
        {{ replaceName(pd_content.rc_secondary_user_details.pi_heading) }}
        <EditLink
          page-name="joint-contact-information"
          is-not-istock="true"
        />
      </h2>
      <apx-phone
        v-if="su_dayPhone"
        :checked="su_mobilePhoneCheckIndicator"
        :telephone-number-label="pd_content.rc_secondary_user_details.pi_phone_no_label"
        :telephone-number="su_dayPhone"
        readonly
      />
      <apx-email
        :label="pd_content.rc_secondary_user_details.pi_email_label"
        :value="su_email"
        readonly
      />
      <apx-select
        :value="residenceCountryName"
        :label="pd_content.rc_secondary_user_details.pi_citizenship_label"
        readonly
      />
      <ReadbackComponent
        :label="pd_content.rc_secondary_user_details.pi_resi_address_label"
        :values="[su_leg_line1, su_leg_line2, cityStateZip]"
      />
    </div>
    <!-- Employement information details -->
    <div class="pvd-container container-divider">
      <h2 class="pvd-heading-L">
        {{ replaceName(pd_content.rc_secondary_user_details.ei_heading) }}
        <EditLink
          page-name="joint-employment-information"
          is-not-istock="true"
        />
      </h2>
      <apx-select
        :value="pd_content.employment_status_mapping[su_employmentStatus]"
        :label="pd_content.rc_secondary_user_details.ei_employment_status_label"
        readonly
      />
      <apx-text-input
        v-if="showEmployerInfo"
        :value="su_occupation? su_occupation : pd_content.none_value_text"
        :label="pd_content.rc_secondary_user_details.ei_occupation_label"
        readonly
      />
      <apx-text-input
        v-if="showEmployerInfo"
        :value="su_employersName? su_employersName : pd_content.none_value_text"
        :label="pd_content.rc_secondary_user_details.ei_company_name_label"
        readonly
      />
      <ReadbackComponent
        v-if="showEmployerInfo"
        :label="pd_content.rc_secondary_user_details.ei_company_address_label"
        :values="employerAddress"
      />
      <ReadbackComponent
        v-if="su_employmentStatus != 'Z'"
        :id="'youth-associated-company-info'"
        :label="pd_content.rc_secondary_user_details.ei_investment_industry_association_label"
        :values="su_coAffillName ? [su_coAffillName] : [pd_content.none_value_text]"
        show-help
        :help-header="pd_content.rc_secondary_user_details.ei_investment_industry_association_help_header"
        :help-body="replaceName(pd_content.rc_secondary_user_details.ei_investment_industry_association_help_body)"
      />
      <ReadbackComponent
        :id="'youth-corporate-control-status'"
        :label="pd_content.rc_secondary_user_details.ei_corporate_control_status_label"
        :values="su_coDirectorSymbol ? [su_coDirectorSymbol] : [pd_content.none_value_text]"
        show-help
        :help-header="pd_content.rc_secondary_user_details.ei_corporate_control_status_help_header"
        :help-body="replaceName(pd_content.rc_secondary_user_details.ei_corporate_control_status_help_body)"
      />
    </div>
    <!-- Tax Disclosure and notice -->
    <div
      v-if="youth"
      class="pvd-container container-divider"
    >
      <h2 class="pvd-heading-L">
        {{ replaceName(pd_content.rc_secondary_user_details.tax_disclosure_header) }}
      </h2>
      <p class="text-italic">
        {{ replaceName(pd_content.rc_secondary_user_details.tax_disclosure_paragraph) }}
      </p>
      <VueShowdown
        :extensions="[formatMarkdown]"
        :markdown="replaceName(pd_content.rc_secondary_user_details.tax_disclosure_list)"
      />
      <InputConfirm
        :label="replaceName(pd_content.rc_secondary_user_details.tax_disclosure_confirm)"
        :id-prefix="idPrefixTax"
        :name="idPrefixTax"
        :label-class="'text-italic'"
        :tax-holding.sync="su_taxWithHolding"
        :track-events="true"
      />
    </div>
    <!-- Before you open your account -->
    <div
      v-if="youth"
      class="pvd-container container-divider"
    >
      <h2 class="pvd-heading-L">
        {{ pd_content.rc_youth_submit_request_01_heading.title }}
      </h2>
      <p class="m-small-top">
        {{ replaceName(pd_content.rc_youth_submit_request_01_heading.paragraph) }}
      </p>
      <ul class="pvd-list-bullet">
        <li>
          <a
            class="pvd-anchor"
            href="javascript:void(0);"
            @click.prevent="handleCustomerAgreementClick()"
          > {{
            pd_content.rc_youth_submit_request_02_bullet1.value }}</a>
        </li>
        <li>
          <a
            class="pvd-anchor"
            href="javascript:void(0);"
            @click.prevent="handleParentAgreementClick()"
          > {{
            pd_content.rc_youth_submit_request_03_bullet2.value }}</a>
        </li>
        <li>
          <a
            class="pvd-anchor"
            href="javascript:void(0);"
            @click.prevent="handleAccountAgreementDisclosure()"
          > {{
            pd_content.rc_youth_submit_request_04_bullet3.value }}</a> {{
            pd_content.rc_youth_submit_request_04_bullet3.paragraph }}
        </li>
        <li v-if="youth && !isDBSPWCParam">
          <a
            class="pvd-anchor"
            href="javascript:void(0);"
            @click.prevent="handleCorePositionAgreementClick()"
          > {{
            pd_content.rc_core_position_agreement.spaxx_url_label }}</a> {{
            pd_content.rc_core_position_agreement.spaxx_url_desc }}
        </li>
        <li>
          <a
            class="pvd-anchor"
            href="javascript:void(0);"
            @click.prevent="handleDebitCardAgreementClick()"
          > {{
            pd_content.rc_youth_submit_request_05_bullet4.value }}</a> {{
            pd_content.rc_youth_submit_request_05_bullet4.paragraph }}
        </li>
        <li>
          <a
            class="pvd-anchor"
            href="javascript:void(0);"
            @click.prevent="handleAlertUserAgreementClick()"
          > {{
            pd_content.rc_youth_submit_request_06_bullet5.value }}</a> {{
            pd_content.rc_youth_submit_request_06_bullet5.paragraph }}
        </li>
        <li>
          <a
            class="pvd-anchor"
            href="javascript:void(0);"
            @click.prevent="handleElectronicServicesAgreementClick()"
          > {{
            pd_content.rc_youth_submit_request_07_bullet6.value }}</a> {{
            pd_content.rc_youth_submit_request_07_bullet6.paragraph }}
        </li>

        <li>
          <a
            class="pvd-anchor"
            href="javascript:void(0);"
            @click.prevent="handleElectronicDeliveryAgreementClick()"
          > {{
            pd_content.rc_electronic_delivery_agreement_pdf.value }}</a> {{
            pd_content.rc_electronic_delivery_agreement_pdf.text }}
        </li>
      </ul>
      <!--   TODO Please modify v-if for managed account -->
      <template v-if="youth">
        <p class="m-small-top">
          {{ pd_content.rc_agremeents_ledge2_youth.heading }}
        </p>
        <ul class="pvd-list-bullet">
          <li>
            {{ pd_content.rc_agremeents_ledge2_youth.para1 }}
          </li>
          <li v-if="isDBSPWCParam">
            <div>{{ pd_content.rc_core_position_agreement.fcash_agreement_1 }}</div>
            <p>&nbsp;</p>
            <div>{{ pd_content.rc_core_position_agreement.fcash_agreement_2 }}</div>
          </li>
          <li v-if="!isDBSPWCParam">
            {{ pd_content.rc_core_position_agreement.spaxx_agreement }}
          </li>
          <li>
            {{ pd_content.rc_agremeents_ledge2_youth.para2 }}
          </li>
          <li>
            {{ pd_content.rc_agremeents_ledge2_youth.para3 }}
          </li>
          <li>
            {{ pd_content.rc_agremeents_ledge2_youth.para4 }}
          </li>
          <li>
            <p>
              <VueShowdown
                :extensions="[formatMarkdown]"
                :markdown="replaceName(request_09_paragraph_agreement)"
              />
            </p>
            <div class="pvd-checkbox m-large-top">
              <input
                :id="YOUTH_PARENT_AGREEMENT"
                v-model="$globalData.jointContactInformation.primaryUserAgreed"
                v-validate="'required'"
                :name="YOUTH_PARENT_AGREEMENT"
                :type="'checkbox'"
                :aria-describedby="errors.has(YOUTH_PARENT_AGREEMENT) ?
                  `${pd_content.rc_youth_submit_request_09_paragraph.agree_error}` : null"
              >
              <label :for="YOUTH_PARENT_AGREEMENT">
                <span
                  class="checkmark"
                  aria-hidden="true"
                >
                  <pvd-icon icon="checkmark" />
                </span>
                {{ replaceName(pd_content.rc_youth_submit_request_09_paragraph.checkbox1) }}
              </label>
            </div>
            <div class="pvd-checkbox m-large-top">
              <input
                :id="YOUTH_CHILD_AGREEMENT"
                v-model="$globalData.jointContactInformation.secondaryUserAgreed"
                v-validate="'required'"
                :name="YOUTH_CHILD_AGREEMENT"
                :type="'checkbox'"
                :aria-describedby="errors.has(YOUTH_CHILD_AGREEMENT)
                  ? `${pd_content.rc_youth_submit_request_09_paragraph.agree_error}` : null"
              >
              <label :for="YOUTH_CHILD_AGREEMENT">
                <span
                  class="checkmark"
                  aria-hidden="true"
                >
                  <pvd-icon icon="checkmark" />
                </span>
                {{ replaceName(pd_content.rc_youth_submit_request_09_paragraph.checkbox2) }}
              </label>
            </div>
            <pvd-system-message
              v-if="errors.has(YOUTH_PARENT_AGREEMENT) || errors.has(YOUTH_CHILD_AGREEMENT)"
              :id="'system-message-youth-agreement'"
              class="m-medium-top"
              placement="inline"
              type="alert"
              :message="pd_content.rc_youth_submit_request_09_paragraph.agree_error"
            />
          </li>
          <li>
            <strong>
              {{ pd_content.rc_agremeents_ledge2_youth.para5 }}
            </strong>
          </li>
          <li>
            <strong>
              {{ pd_content.rc_agremeents_ledge2_youth.para6 }}
            </strong>
          </li>
        </ul>
      </template>
      <template v-if="!youth">
        <p>
          <strong>
            {{ pd_content.rc_youth_submit_request_09_paragraph.p1 }}
            <a
              class="pvd-anchor"
              href="javascript:void(0);"
              @click.prevent="handlePredisputeArbitrationClick()"
            >{{
              pd_content.rc_youth_submit_request_09_paragraph.urlText }}</a>{{
              pd_content.rc_youth_submit_request_09_paragraph.p2 }}
          </strong>
        </p>
        <p>
          <VueShowdown
            :extensions="[formatMarkdown]"
            :markdown="replaceName(request_09_paragraph_agreement)"
          />
        </p>
        <div class="pvd-checkbox m-large-top">
          <input
            :id="YOUTH_PARENT_AGREEMENT"
            v-model="$globalData.jointContactInformation.primaryUserAgreed"
            v-validate="'required'"
            :name="YOUTH_PARENT_AGREEMENT"
            :type="'checkbox'"
            :aria-describedby="errors.has(YOUTH_PARENT_AGREEMENT)
              ? `${pd_content.rc_youth_submit_request_09_paragraph.agree_error}` : null"
          >
          <label :for="YOUTH_PARENT_AGREEMENT">
            <span
              class="checkmark"
              aria-hidden="true"
            >
              <pvd-icon icon="checkmark" />
            </span>
            {{ replaceName(pd_content.rc_youth_submit_request_09_paragraph.checkbox1) }}
          </label>
        </div>
        <div class="pvd-checkbox m-large-top">
          <input
            :id="YOUTH_CHILD_AGREEMENT"
            v-model="$globalData.jointContactInformation.secondaryUserAgreed"
            v-validate="'required'"
            :name="YOUTH_CHILD_AGREEMENT"
            :type="'checkbox'"
            :aria-describedby="errors.has(YOUTH_CHILD_AGREEMENT)
              ? `${pd_content.rc_youth_submit_request_09_paragraph.agree_error}` : null"
          >
          <label :for="YOUTH_CHILD_AGREEMENT">
            <span
              class="checkmark"
              aria-hidden="true"
            >
              <pvd-icon icon="checkmark" />
            </span>
            {{ replaceName(pd_content.rc_youth_submit_request_09_paragraph.checkbox2) }}
          </label>
        </div>
        <pvd-system-message
          v-if="errors.has(YOUTH_PARENT_AGREEMENT) || errors.has(YOUTH_CHILD_AGREEMENT)"
          :id="'system-message-youth-agreement'"
          class="m-medium-top"
          placement="inline"
          type="alert"
          :message="pd_content.rc_youth_submit_request_09_paragraph.agree_error"
        />
        <p class="text-italic text-bold">
          {{ pd_content.rc_youth_submit_request_09_paragraph.consent }}
        </p>
      </template>
    </div>
  </div>
</template>

<script>
import dataHelper from '../mixins/dataHelper';
import youthHelper from '../mixins/youthHelper';
import EditLink from './EditLink.vue';
import ReadbackComponent from './ReadbackComponent.vue';
import InputConfirm from './InputConfirm.vue';
import utility from '../mixins/utility';
import constants from '../mixins/constants';

export default {
  name: 'SecondaryUserDetailsComponent',
  components: {
    EditLink,
    ReadbackComponent,
    InputConfirm,
  },
  mixins: [
    dataHelper,
    utility,
    youthHelper,
  ],
  inject: ['$validator'],
  props: {
    prospectusUrl: {
      type: String,
      default: () => '',
    },
  },
  data() {
    return {
      idPrefixTax: constants.YOUTH_TAX_AGREEMENT,
      YOUTH_PARENT_AGREEMENT: constants.YOUTH_PARENT_AGREEMENT,
      YOUTH_CHILD_AGREEMENT: constants.YOUTH_CHILD_AGREEMENT,
    };
  },
  computed: {
    isDBSPWCParam() {
      return ((this.queryParams.dbs || '').toUpperCase() === 'PWC');
    },
    request_09_paragraph_agreement() {
      return this.isDBSPWCParam ? this.pd_content.rc_youth_submit_request_09_paragraph.agreement_dbs_pwc
        : this.pd_content.rc_youth_submit_request_09_paragraph.agreement;
    },
    residenceCountryName() {
      let countryValue;
      if (this.su_residenceCountry !== '999') {
        const countryObject = this.pd_countryList.validCitizenship
          .find((element) => element.value === this.su_residenceCountry);
        countryValue = countryObject.option;
      } else {
        countryValue = 'UNITED STATES';
      }
      return countryValue;
    },
    cityStateZip() {
      let separator = ',';
      if (this.su_leg_state === '' && this.su_leg_zip === '') {
        separator = '';
      }
      return `${this.su_leg_city}${separator} ${this.su_leg_state} ${this.su_leg_zip}`;
    },
    showEmployerInfo() {
      return this.su_employmentStatus !== 'N' && this.su_employmentStatus !== 'R';
    },
    employerAddress() {
      return (this.su_employerAddressSameAsLegal === 'Yes' && this.su_employmentStatus === 'S')
        ? [this.su_leg_line1, this.su_leg_line2, this.cityStateZip] : [
          this.su_emp_line1, this.su_emp_line2, this.employerCityStateZip];
    },
    employerCityStateZip() {
      let separator = ',';
      if (this.su_emp_state === '' && this.su_emp_zip === '') {
        separator = '';
      }
      return `${this.su_emp_city}${separator} ${this.su_emp_state} ${this.su_emp_zip}`;
    },
    youth() {
      return this.routeId === 'youth';
    },
  },
  methods: {
    handleCustomerAgreementClick() {
      this.openWindow(this.pd_content.rc_youth_submit_request_02_bullet1.url, 'mainwindow', '1024', '500');
    },
    handleParentAgreementClick() {
      this.openWindow(this.pd_content.rc_youth_submit_request_03_bullet2.url, 'mainwindow', '1024', '500');
    },
    handleAccountAgreementDisclosure() {
      this.openWindow(this.pd_content.rc_youth_submit_request_04_bullet3.url, 'mainwindow', '1024', '500');
    },
    handleCorePositionAgreementClick() {
      const url = this.pd_content.rc_spaxx_prospectus_pdf ? this.pd_content.rc_spaxx_prospectus_pdf.url : '';
      this.openWindow(url, 'documentwindow', '1024', '500');
    },
    handleDebitCardAgreementClick() {
      this.openWindow(this.pd_content.rc_youth_submit_request_05_bullet4.url, 'mainwindow', '1024', '500');
    },
    handleAlertUserAgreementClick() {
      this.openWindow(this.pd_content.rc_youth_submit_request_06_bullet5.url, 'mainwindow', '1024', '500');
    },
    handleElectronicServicesAgreementClick() {
      this.openWindow(this.pd_content.rc_youth_submit_request_07_bullet6.url, 'mainwindow', '1024', '500');
    },
    handleElectronicDeliveryAgreementClick() {
      this.openWindow(this.pd_content.rc_electronic_delivery_agreement_pdf.url, 'mainwindow', '1024', '500');
    },
    handleSPAXXProspectusClick() {
      this.openWindow(decodeURIComponent(this.prospectusUrl), 'mainwindow', '1024', '500');
    },
    handlePredisputeArbitrationClick() {
      this.openWindow(this.pd_content.rc_youth_submit_request_09_paragraph.url, 'mainwindow', '1024', '500');
    },
  },
};
</script>
<style scoped>
</style>
